// icons.ts — geometric in-engine glyphs (style-bible v2, Tactical OLED Arcade). A dependency-free
// stand-in for the handoff's Phosphor-Fill UI icon language: each glyph is drawn with Phaser
// Graphics primitives, centered in an s×s box at the local origin, in a single tintable color. This
// replaces ad-hoc system emoji (📊/⚙/🔒/✓…) with one cohesive, crisp, colorblind-neutral set that
// renders identically on every platform and survives at HUD size. No fonts, no textures, no deps.
//
// Usage:
//   const g = icon(scene, 'bolt', x, y, 18, PALETTE.correct);   // -> positioned Graphics
//   drawIcon(existingGraphics, 'heart', 16, PALETTE.hazard);    // -> draw at local (0,0)
import Phaser from 'phaser';

export type IconName =
  | 'chomp'
  | 'scan'
  | 'heart'
  | 'bolt'
  | 'lock'
  | 'check'
  | 'x'
  | 'gear'
  | 'question'
  | 'info'
  | 'crown'
  | 'target'
  | 'flame'
  | 'palette'
  | 'chart'
  | 'trophy'
  | 'shuffle'
  | 'share'
  | 'again'
  | 'play'
  | 'pause'
  | 'shield'
  | 'caretRight'
  | 'caretLeft'
  | 'caretDown'
  | 'plus'
  | 'star'
  | 'gauge'
  | 'wifiOff'
  | 'sliders'
  | 'medal'
  | 'clock'
  | 'lightbulb'
  | 'flask'
  | 'circleNotch'
  | 'checkCircle'
  | 'xCircle'
  | 'copy'
  | 'image'
  | 'house'
  | 'ranking'
  | 'grip'
  | 'sword'
  | 'snowflake'
  | 'sparkle'
  | 'infinity'
  | 'eyeOff'
  | 'minus'
  | 'arrowUp'
  | 'cpu'
  | 'link';

const rad = Phaser.Math.DegToRad;

/** Draw an icon centered at the Graphics' local origin, in an s×s box. Sets its own fill/stroke. */
export function drawIcon(g: Phaser.GameObjects.Graphics, name: IconName, s: number, color: number, alpha = 1): void {
  const t = Math.max(1.6, s * 0.11); // bold stroke weight for line-based glyphs
  g.fillStyle(color, alpha);
  g.lineStyle(t, color, alpha);
  switch (name) {
    case 'chomp': {
      // pac-style chomper: a disc with an open mouth wedge to the right (the CHOMP verb)
      const r = s * 0.46;
      g.beginPath();
      g.slice(0, 0, r, rad(33), rad(327), false);
      g.fillPath();
      g.fillStyle(0x000000, 0); // (no-op: keep eye optional; primary use is monochrome)
      g.fillStyle(color, alpha);
      break;
    }
    case 'scan': {
      // magnifying glass: ring + handle
      const cr = s * 0.30;
      const cx = -s * 0.08;
      const cy = -s * 0.08;
      g.strokeCircle(cx, cy, cr);
      g.lineBetween(cx + cr * 0.72, cy + cr * 0.72, s * 0.42, s * 0.42);
      break;
    }
    case 'heart': {
      const lobeR = s * 0.235;
      const lx = s * 0.2;
      const ly = s * 0.12;
      g.fillCircle(-lx, -ly, lobeR);
      g.fillCircle(lx, -ly, lobeR);
      g.fillTriangle(-lx - lobeR * 0.96, -ly + lobeR * 0.34, lx + lobeR * 0.96, -ly + lobeR * 0.34, 0, s * 0.46);
      break;
    }
    case 'bolt': {
      const p = [
        [0.1, -0.5],
        [-0.3, 0.1],
        [-0.03, 0.1],
        [-0.16, 0.5],
        [0.32, -0.12],
        [0.05, -0.12],
      ].map(([x, y]) => new Phaser.Geom.Point(x * s, y * s));
      g.fillPoints(p, true);
      break;
    }
    case 'lock': {
      // shackle (stroked arc) + rounded body
      g.beginPath();
      g.arc(0, -s * 0.08, s * 0.17, rad(180), rad(360), false);
      g.strokePath();
      g.lineBetween(-s * 0.17, -s * 0.08, -s * 0.17, s * 0.02);
      g.lineBetween(s * 0.17, -s * 0.08, s * 0.17, s * 0.02);
      g.fillRoundedRect(-s * 0.3, s * 0.0, s * 0.6, s * 0.42, s * 0.1);
      break;
    }
    case 'check': {
      g.lineStyle(t * 1.35, color, alpha);
      g.beginPath();
      g.moveTo(-s * 0.3, s * 0.02);
      g.lineTo(-s * 0.06, s * 0.26);
      g.lineTo(s * 0.34, -s * 0.28);
      g.strokePath();
      break;
    }
    case 'x': {
      g.lineStyle(t * 1.25, color, alpha);
      const a = s * 0.28;
      g.lineBetween(-a, -a, a, a);
      g.lineBetween(-a, a, a, -a);
      break;
    }
    case 'gear': {
      // toothed wheel (stroked) + hub ring
      const teeth = 8;
      const ro = s * 0.44;
      const ri = s * 0.34;
      const pts: Phaser.Geom.Point[] = [];
      for (let i = 0; i < teeth * 2; i++) {
        const ang = (Math.PI / teeth) * i;
        const r = i % 2 === 0 ? ro : ri;
        pts.push(new Phaser.Geom.Point(Math.cos(ang) * r, Math.sin(ang) * r));
      }
      g.lineStyle(t, color, alpha);
      g.strokePoints(pts, true, true);
      g.strokeCircle(0, 0, s * 0.16);
      break;
    }
    case 'sliders': {
      // settings sliders: 3 tracks with knobs
      g.lineStyle(Math.max(2, s * 0.09), color, alpha);
      const ys = [-s * 0.26, 0, s * 0.26];
      const kx = [s * 0.16, -s * 0.14, s * 0.05];
      ys.forEach((y, i) => {
        g.lineBetween(-s * 0.36, y, s * 0.36, y);
        g.fillCircle(kx[i], y, s * 0.1);
      });
      break;
    }
    case 'question': {
      // 3/4 bowl (left→top→right→down-right) + a stem angling in to the vertical centre + a dot.
      const lw = Math.max(1.8, s * 0.11);
      g.lineStyle(lw, color, alpha);
      g.beginPath();
      g.arc(0, -s * 0.13, s * 0.18, rad(150), rad(60), false);
      g.strokePath();
      const ex = Math.cos(rad(60)) * s * 0.18;
      const ey = -s * 0.13 + Math.sin(rad(60)) * s * 0.18;
      g.lineBetween(ex, ey, 0, s * 0.12);
      g.fillCircle(0, s * 0.34, s * 0.092);
      break;
    }
    case 'info': {
      g.fillCircle(0, -s * 0.24, s * 0.09);
      g.fillRoundedRect(-s * 0.085, -s * 0.06, s * 0.17, s * 0.36, s * 0.06);
      break;
    }
    case 'crown': {
      const b = s * 0.26;
      const top = -s * 0.24;
      const p = [
        [-0.42, 0.26],
        [-0.42, -0.18],
        [-0.18, 0.04],
        [0.0, b / s - 0.5],
        [0.18, 0.04],
        [0.42, -0.18],
        [0.42, 0.26],
      ].map(([x, y]) => new Phaser.Geom.Point(x * s, y * s));
      // recompute peak using `top`
      p[3] = new Phaser.Geom.Point(0, top);
      g.fillPoints(p, true);
      break;
    }
    case 'target': {
      g.lineStyle(t, color, alpha);
      g.strokeCircle(0, 0, s * 0.42);
      g.strokeCircle(0, 0, s * 0.24);
      g.fillCircle(0, 0, s * 0.09);
      break;
    }
    case 'flame': {
      const p = [
        [0.0, -0.5],
        [0.28, -0.12],
        [0.34, 0.18],
        [0.16, 0.46],
        [-0.16, 0.46],
        [-0.34, 0.18],
        [-0.22, -0.06],
        [-0.02, 0.12],
        [0.06, -0.16],
      ].map(([x, y]) => new Phaser.Geom.Point(x * s, y * s));
      g.fillPoints(p, true);
      break;
    }
    case 'palette': {
      // ring outline + thumb hole + paint dabs (reads as an artist's palette in one color)
      g.lineStyle(Math.max(1.6, s * 0.08), color, alpha);
      g.strokeCircle(0, 0, s * 0.42);
      g.strokeCircle(s * 0.17, s * 0.2, s * 0.1); // thumb hole
      g.fillStyle(color, alpha);
      g.fillCircle(-s * 0.17, -s * 0.13, s * 0.062);
      g.fillCircle(s * 0.04, -s * 0.21, s * 0.062);
      g.fillCircle(s * 0.22, -s * 0.02, s * 0.062);
      g.fillCircle(-s * 0.22, s * 0.1, s * 0.062);
      break;
    }
    case 'chart': {
      const base = s * 0.4;
      const bw = s * 0.18;
      const xs = [-s * 0.32, -s * 0.04, s * 0.24];
      const hs = [s * 0.34, s * 0.56, s * 0.78];
      xs.forEach((x, i) => g.fillRect(x, base - hs[i], bw, hs[i]));
      break;
    }
    case 'trophy':
    case 'medal': {
      // cup bowl (top rounded rect + a taper to the stem) + side handles + stem + base
      g.fillStyle(color, alpha);
      g.fillRoundedRect(-s * 0.24, -s * 0.4, s * 0.48, s * 0.26, { tl: s * 0.06, tr: s * 0.06, bl: 0, br: 0 });
      g.fillTriangle(-s * 0.22, -s * 0.16, s * 0.22, -s * 0.16, 0, s * 0.04);
      // handles (open arcs hugging the bowl)
      g.lineStyle(Math.max(1.6, s * 0.07), color, alpha);
      g.beginPath();
      g.arc(-s * 0.3, -s * 0.3, s * 0.12, rad(70), rad(290), true);
      g.strokePath();
      g.beginPath();
      g.arc(s * 0.3, -s * 0.3, s * 0.12, rad(250), rad(110), false);
      g.strokePath();
      // stem + base
      g.fillStyle(color, alpha);
      g.fillRect(-s * 0.05, s * 0.02, s * 0.1, s * 0.16);
      g.fillRoundedRect(-s * 0.2, s * 0.18, s * 0.4, s * 0.1, s * 0.03);
      break;
    }
    case 'arrowUp': {
      g.fillTriangle(0, -s * 0.42, -s * 0.32, -s * 0.04, s * 0.32, -s * 0.04);
      g.fillRect(-s * 0.11, -s * 0.06, s * 0.22, s * 0.44);
      break;
    }
    case 'shuffle': {
      g.lineStyle(t, color, alpha);
      g.lineBetween(-s * 0.4, -s * 0.22, -s * 0.06, -s * 0.22);
      g.lineBetween(-s * 0.06, -s * 0.22, s * 0.34, s * 0.22);
      g.lineBetween(-s * 0.4, s * 0.22, -s * 0.06, s * 0.22);
      g.lineBetween(-s * 0.06, s * 0.22, s * 0.16, -s * 0.02);
      g.fillTriangle(s * 0.22, -s * 0.34, s * 0.22, -s * 0.1, s * 0.42, -s * 0.22);
      g.fillTriangle(s * 0.22, s * 0.34, s * 0.22, s * 0.1, s * 0.42, s * 0.22);
      break;
    }
    case 'share': {
      const r = s * 0.11;
      const a = { x: -s * 0.26, y: 0 };
      const b = { x: s * 0.26, y: -s * 0.3 };
      const c = { x: s * 0.26, y: s * 0.3 };
      g.lineStyle(Math.max(2, s * 0.08), color, alpha);
      g.lineBetween(a.x, a.y, b.x, b.y);
      g.lineBetween(a.x, a.y, c.x, c.y);
      g.fillCircle(a.x, a.y, r);
      g.fillCircle(b.x, b.y, r);
      g.fillCircle(c.x, c.y, r);
      break;
    }
    case 'again': {
      // circular arrow (refresh)
      g.lineStyle(t, color, alpha);
      g.beginPath();
      g.arc(0, 0, s * 0.34, rad(70), rad(330), false);
      g.strokePath();
      // arrowhead at the start of the arc
      const ax = Math.cos(rad(70)) * s * 0.34;
      const ay = Math.sin(rad(70)) * s * 0.34;
      g.fillTriangle(ax - s * 0.14, ay - s * 0.02, ax + s * 0.04, ay - s * 0.16, ax + s * 0.12, ay + s * 0.08);
      break;
    }
    case 'play':
    case 'caretRight': {
      const w = name === 'play' ? 0.34 : 0.22;
      g.fillTriangle(-w * s, -s * 0.34, -w * s, s * 0.34, s * 0.36, 0);
      break;
    }
    case 'caretLeft': {
      g.fillTriangle(0.22 * s, -s * 0.34, 0.22 * s, s * 0.34, -s * 0.36, 0);
      break;
    }
    case 'caretDown': {
      g.fillTriangle(-s * 0.34, -0.22 * s, s * 0.34, -0.22 * s, 0, s * 0.36);
      break;
    }
    case 'pause': {
      const bw = s * 0.16;
      g.fillRoundedRect(-s * 0.26, -s * 0.32, bw, s * 0.64, s * 0.04);
      g.fillRoundedRect(s * 0.1, -s * 0.32, bw, s * 0.64, s * 0.04);
      break;
    }
    case 'shield': {
      const p = [
        [0.0, -0.46],
        [0.4, -0.3],
        [0.4, 0.06],
        [0.0, 0.48],
        [-0.4, 0.06],
        [-0.4, -0.3],
      ].map(([x, y]) => new Phaser.Geom.Point(x * s, y * s));
      g.fillPoints(p, true);
      break;
    }
    case 'plus': {
      g.lineStyle(t * 1.2, color, alpha);
      g.lineBetween(-s * 0.3, 0, s * 0.3, 0);
      g.lineBetween(0, -s * 0.3, 0, s * 0.3);
      break;
    }
    case 'minus': {
      g.lineStyle(t * 1.2, color, alpha);
      g.lineBetween(-s * 0.3, 0, s * 0.3, 0);
      break;
    }
    case 'star': {
      const pts: Phaser.Geom.Point[] = [];
      for (let i = 0; i < 10; i++) {
        const ang = rad(-90 + i * 36);
        const r = i % 2 === 0 ? s * 0.46 : s * 0.2;
        pts.push(new Phaser.Geom.Point(Math.cos(ang) * r, Math.sin(ang) * r));
      }
      g.fillPoints(pts, true);
      break;
    }
    case 'gauge': {
      g.lineStyle(t, color, alpha);
      g.beginPath();
      g.arc(0, s * 0.1, s * 0.4, rad(160), rad(20), false);
      g.strokePath();
      g.lineBetween(0, s * 0.1, s * 0.18, -s * 0.18);
      g.fillCircle(0, s * 0.1, s * 0.07);
      break;
    }
    case 'wifiOff': {
      g.lineStyle(t * 0.9, color, alpha);
      g.beginPath();
      g.arc(0, s * 0.22, s * 0.42, rad(210), rad(330), false);
      g.strokePath();
      g.beginPath();
      g.arc(0, s * 0.22, s * 0.24, rad(210), rad(330), false);
      g.strokePath();
      g.fillCircle(0, s * 0.22, s * 0.07);
      g.lineStyle(t * 0.9, color, alpha);
      g.lineBetween(-s * 0.4, -s * 0.4, s * 0.4, s * 0.4); // slash
      break;
    }
    case 'clock': {
      g.lineStyle(t, color, alpha);
      g.strokeCircle(0, 0, s * 0.42);
      g.lineBetween(0, 0, 0, -s * 0.26);
      g.lineBetween(0, 0, s * 0.2, s * 0.06);
      g.fillCircle(0, 0, s * 0.06);
      break;
    }
    case 'lightbulb': {
      g.fillCircle(0, -s * 0.1, s * 0.3);
      g.fillRect(-s * 0.16, s * 0.12, s * 0.32, s * 0.14);
      g.lineStyle(Math.max(2, s * 0.08), color, alpha);
      g.lineBetween(-s * 0.13, s * 0.3, s * 0.13, s * 0.3);
      g.lineBetween(-s * 0.1, s * 0.4, s * 0.1, s * 0.4);
      break;
    }
    case 'flask': {
      g.lineStyle(Math.max(2, s * 0.085), color, alpha);
      g.beginPath();
      g.moveTo(-s * 0.13, -s * 0.4);
      g.lineTo(-s * 0.13, -s * 0.06);
      g.lineTo(-s * 0.34, s * 0.36);
      g.lineTo(s * 0.34, s * 0.36);
      g.lineTo(s * 0.13, -s * 0.06);
      g.lineTo(s * 0.13, -s * 0.4);
      g.strokePath();
      g.lineBetween(-s * 0.2, -s * 0.4, s * 0.2, -s * 0.4);
      g.fillStyle(color, alpha);
      g.fillTriangle(-s * 0.24, s * 0.18, s * 0.24, s * 0.18, 0, -s * 0.02);
      break;
    }
    case 'circleNotch': {
      // 3/4 arc spinner
      g.lineStyle(t * 1.1, color, alpha);
      g.beginPath();
      g.arc(0, 0, s * 0.38, rad(60), rad(330), false);
      g.strokePath();
      break;
    }
    case 'checkCircle': {
      g.lineStyle(Math.max(1.6, s * 0.08), color, alpha);
      g.strokeCircle(0, 0, s * 0.44);
      g.lineStyle(t * 1.2, color, alpha);
      g.beginPath();
      g.moveTo(-s * 0.2, s * 0.01);
      g.lineTo(-s * 0.04, s * 0.18);
      g.lineTo(s * 0.24, -s * 0.18);
      g.strokePath();
      break;
    }
    case 'xCircle': {
      g.lineStyle(Math.max(1.6, s * 0.08), color, alpha);
      g.strokeCircle(0, 0, s * 0.44);
      g.lineStyle(t * 1.1, color, alpha);
      const a = s * 0.18;
      g.lineBetween(-a, -a, a, a);
      g.lineBetween(-a, a, a, -a);
      break;
    }
    case 'copy': {
      g.lineStyle(Math.max(1.8, s * 0.085), color, alpha);
      g.strokeRoundedRect(-s * 0.34, -s * 0.34, s * 0.46, s * 0.46, s * 0.07);
      g.strokeRoundedRect(-s * 0.1, -s * 0.1, s * 0.46, s * 0.46, s * 0.07);
      break;
    }
    case 'image': {
      g.lineStyle(Math.max(1.8, s * 0.08), color, alpha);
      g.strokeRoundedRect(-s * 0.4, -s * 0.32, s * 0.8, s * 0.64, s * 0.08);
      g.fillCircle(-s * 0.15, -s * 0.1, s * 0.08);
      g.fillTriangle(-s * 0.34, s * 0.26, -s * 0.02, -s * 0.04, s * 0.16, s * 0.26);
      g.fillTriangle(s * 0.02, s * 0.26, s * 0.22, s * 0.02, s * 0.38, s * 0.26);
      break;
    }
    case 'house': {
      g.fillTriangle(-s * 0.42, -s * 0.02, s * 0.42, -s * 0.02, 0, -s * 0.42);
      g.fillRoundedRect(-s * 0.3, -s * 0.04, s * 0.6, s * 0.44, s * 0.04);
      break;
    }
    case 'ranking': {
      // winner's-stand podium: a shared base plinth + three stepped tops (center tallest), with a '1'
      // floating above the center step so it reads as 1st-place — not a free-floating bar chart.
      const baseY = s * 0.34;
      g.fillRoundedRect(-s * 0.46, baseY, s * 0.92, s * 0.12, s * 0.03); // connecting plinth
      g.fillRoundedRect(-s * 0.44, s * 0.04, s * 0.26, baseY - s * 0.04, s * 0.03); // left (3rd)
      g.fillRoundedRect(-s * 0.13, -s * 0.18, s * 0.26, baseY + s * 0.18, { tl: s * 0.04, tr: s * 0.04, bl: 0, br: 0 }); // center (1st, tallest)
      g.fillRoundedRect(s * 0.18, -s * 0.06, s * 0.26, baseY + s * 0.06, s * 0.03); // right (2nd)
      // '1' floating above the center step
      g.lineStyle(Math.max(1.8, s * 0.085), color, alpha);
      g.lineBetween(0, -s * 0.46, 0, -s * 0.26);
      g.lineBetween(-s * 0.08, -s * 0.4, 0, -s * 0.46);
      break;
    }
    case 'cpu': {
      // microchip: rounded body + inner core + pins on all four edges (the "AI / processor" glyph)
      g.lineStyle(Math.max(1.8, s * 0.08), color, alpha);
      g.strokeRoundedRect(-s * 0.26, -s * 0.26, s * 0.52, s * 0.52, s * 0.05);
      g.fillRect(-s * 0.1, -s * 0.1, s * 0.2, s * 0.2);
      for (const o of [-s * 0.13, 0, s * 0.13]) {
        g.lineBetween(o, -s * 0.26, o, -s * 0.4);
        g.lineBetween(o, s * 0.26, o, s * 0.4);
        g.lineBetween(-s * 0.26, o, -s * 0.4, o);
        g.lineBetween(s * 0.26, o, s * 0.4, o);
      }
      break;
    }
    case 'link': {
      // chain link: two overlapping capsules joined through the middle (URL / share-link glyph)
      const lw = Math.max(2, s * 0.09);
      g.lineStyle(lw, color, alpha);
      g.strokeRoundedRect(-s * 0.4, -s * 0.15, s * 0.42, s * 0.3, s * 0.15);
      g.strokeRoundedRect(-s * 0.02, -s * 0.15, s * 0.42, s * 0.3, s * 0.15);
      g.lineBetween(-s * 0.12, 0, s * 0.12, 0);
      break;
    }
    case 'grip': {
      g.lineStyle(Math.max(2, s * 0.12), color, alpha);
      g.lineBetween(-s * 0.3, -s * 0.06, s * 0.3, -s * 0.06);
      g.lineBetween(-s * 0.3, s * 0.12, s * 0.3, s * 0.12);
      break;
    }
    case 'sword': {
      g.lineStyle(Math.max(2, s * 0.09), color, alpha);
      g.lineBetween(-s * 0.3, s * 0.3, s * 0.28, -s * 0.28);
      g.fillTriangle(s * 0.18, -s * 0.42, s * 0.42, -s * 0.18, s * 0.2, -s * 0.16);
      g.lineBetween(-s * 0.34, s * 0.14, -s * 0.14, s * 0.34);
      break;
    }
    case 'snowflake': {
      const lw = Math.max(1.6, s * 0.07);
      g.lineStyle(lw, color, alpha);
      for (let i = 0; i < 3; i++) {
        const ang = rad(i * 60);
        const cx = Math.cos(ang);
        const cy = Math.sin(ang);
        const dx = cx * s * 0.42;
        const dy = cy * s * 0.42;
        g.lineBetween(-dx, -dy, dx, dy);
        // V-branches at each of the 6 tips so it reads as a snowflake, not an asterisk
        for (const sgn of [-1, 1]) {
          const ex = dx * sgn;
          const ey = dy * sgn;
          for (const da of [40, -40]) {
            const bx = Math.cos(ang + rad(da)) * s * 0.16;
            const by = Math.sin(ang + rad(da)) * s * 0.16;
            g.lineBetween(ex, ey, ex - bx * sgn, ey - by * sgn);
          }
        }
      }
      break;
    }
    case 'sparkle': {
      const p = [
        [0, -0.46],
        [0.12, -0.12],
        [0.46, 0],
        [0.12, 0.12],
        [0, 0.46],
        [-0.12, 0.12],
        [-0.46, 0],
        [-0.12, -0.12],
      ].map(([x, y]) => new Phaser.Geom.Point(x * s, y * s));
      g.fillPoints(p, true);
      break;
    }
    case 'infinity': {
      g.lineStyle(Math.max(2.4, s * 0.11), color, alpha);
      g.strokeCircle(-s * 0.2, 0, s * 0.2);
      g.strokeCircle(s * 0.2, 0, s * 0.2);
      break;
    }
    case 'eyeOff': {
      g.lineStyle(Math.max(1.8, s * 0.08), color, alpha);
      g.beginPath();
      g.arc(0, 0, s * 0.4, rad(200), rad(340), false);
      g.strokePath();
      g.beginPath();
      g.arc(0, 0, s * 0.4, rad(20), rad(160), false);
      g.strokePath();
      g.fillCircle(0, 0, s * 0.13);
      g.lineBetween(-s * 0.4, -s * 0.4, s * 0.4, s * 0.4);
      break;
    }
    default:
      g.fillCircle(0, 0, s * 0.3);
  }
}

/** Create a positioned Graphics object with the icon drawn at its local origin. */
export function icon(
  scene: Phaser.Scene,
  name: IconName,
  x: number,
  y: number,
  s: number,
  color: number,
  alpha = 1,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  drawIcon(g, name, s, color, alpha);
  return g;
}
